import { type CSSProperties, useCallback, useEffect, useRef, useState } from 'react';
import { colors } from '@yorukiri/ui/tokens';

type Props = {
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  width?: number;
  disabled?: boolean;
};

// Number input with inline decrement/increment buttons. Keeps focus + caret in
// the text field, holds-to-repeat on press, and clamps to [min, max] on blur
// so the user can freely overtype a value without being cut off mid-edit.
export function Stepper({
  value,
  onChange,
  min = 0,
  max = 9999,
  step = 1,
  suffix,
  width = 110,
  disabled,
}: Props) {
  const [raw, setRaw] = useState<string>(String(value));
  const externalRef = useRef(value);

  // Sync external changes (reset / load) into the text field.
  useEffect(() => {
    if (value !== externalRef.current) {
      externalRef.current = value;
      setRaw(String(value));
    }
  }, [value]);

  const commit = useCallback(
    (next: number) => {
      const clamped = Math.min(max, Math.max(min, Math.round(next / step) * step));
      if (!Number.isFinite(clamped)) return;
      externalRef.current = clamped;
      setRaw(String(clamped));
      if (clamped !== value) onChange(clamped);
    },
    [max, min, step, value, onChange],
  );

  const bump = (delta: number) => commit((Number(raw) || 0) + delta);

  // Press-and-hold to repeat the bump. Start slow, accelerate.
  const holdRef = useRef<number | null>(null);
  const startHold = (delta: number) => {
    bump(delta);
    let interval = 180;
    const tick = () => {
      bump(delta);
      interval = Math.max(35, interval * 0.75);
      holdRef.current = window.setTimeout(tick, interval);
    };
    holdRef.current = window.setTimeout(tick, 380);
  };
  const stopHold = () => {
    if (holdRef.current != null) window.clearTimeout(holdRef.current);
    holdRef.current = null;
  };

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => setRaw(e.target.value);

  const handleBlur = () => {
    const n = Number(raw);
    if (!Number.isFinite(n)) {
      setRaw(String(value));
      return;
    }
    commit(n);
  };

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      bump(step);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      bump(-step);
    } else if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'stretch',
          width,
          height: 30,
          background: colors.surface,
          border: `1px solid ${colors.line2}`,
          borderRadius: 6,
          overflow: 'hidden',
        }}
      >
        <StepperBtn
          label="−"
          disabled={disabled || (Number(raw) || 0) - step < min}
          onStart={() => startHold(-step)}
          onEnd={stopHold}
        />
        <input
          type="text"
          inputMode="numeric"
          value={raw}
          onChange={handleInput}
          onBlur={handleBlur}
          onKeyDown={handleKey}
          disabled={disabled}
          style={{
            flex: 1,
            minWidth: 0,
            border: 'none',
            background: 'transparent',
            color: colors.ink,
            fontFamily: 'inherit',
            fontSize: 13,
            fontVariantNumeric: 'tabular-nums',
            textAlign: 'center',
            outline: 'none',
            padding: 0,
          }}
        />
        <StepperBtn
          label="+"
          disabled={disabled || (Number(raw) || 0) + step > max}
          onStart={() => startHold(step)}
          onEnd={stopHold}
        />
      </div>
      {suffix && <span style={{ color: colors.ink3, fontSize: 12 }}>{suffix}</span>}
    </div>
  );
}

function StepperBtn({
  label,
  disabled,
  onStart,
  onEnd,
}: {
  label: string;
  disabled?: boolean;
  onStart: () => void;
  onEnd: () => void;
}) {
  return (
    <button
      onPointerDown={(e) => {
        if (disabled) return;
        e.preventDefault();
        onStart();
      }}
      onPointerUp={onEnd}
      onPointerLeave={onEnd}
      onPointerCancel={onEnd}
      disabled={disabled}
      tabIndex={-1}
      style={stepBtnStyle(disabled)}
    >
      {label}
    </button>
  );
}

function stepBtnStyle(disabled?: boolean): CSSProperties {
  return {
    width: 26,
    border: 'none',
    borderLeft: 'transparent',
    borderRight: 'transparent',
    background: 'transparent',
    color: disabled ? colors.ink3 : colors.ink2,
    cursor: disabled ? 'default' : 'pointer',
    fontFamily: 'inherit',
    fontSize: 14,
    lineHeight: 1,
    padding: 0,
    userSelect: 'none',
    transition: 'background 0.08s',
  };
}
